import { useEffect, useMemo, useState } from 'react';
import { ErreurApi, type Client } from '../../api/client.js';
import { COULEURS, POLICE_EQUIPE, styleBouton } from '../../app/styles.js';
import { creerSourceComptesRendus, PERMISSION_ECRIRE, PERMISSION_VALIDER } from './comptes-rendus/source.js';
import { VueComptesRendus } from './comptes-rendus/VueComptesRendus.js';
import { creerSourceActesNotaries, PERMISSION_ECRIRE_ACTES } from './actes/source.js';
import { VueActesNotaries } from './actes/VueActesNotaries.js';
import { creerSourceJournal } from './journal/source.js';
import { VueJournal } from './journal/VueJournal.js';
import { NavSections, type VueDossier } from './sections.js';
import { creerSourceImportContacts, PERMISSION_IMPORTER } from '../brigitte/import-contacts/source.js';
import { VueImportContacts } from '../brigitte/import-contacts/VueImportContacts.js';
import { creerSourceVideos, PERMISSION_ECRIRE_VIDEOS, PERMISSION_PARTAGER_VIDEOS } from '../documents/videos/source.js';
import { VueVideos } from '../documents/videos/VueVideos.js';

/**
 * Sous-vue de D03 (PARITE-4) : en-tête du dossier, sections avec l'onglet courant marqué, puis le contenu de l'onglet.
 * Les droits d'écriture viennent des permissions de la session (`auth.moi`, calculées par le serveur sur le sujet effectif).
 */
interface EnTete {
  title: string;
  reference: string;
}
interface Session {
  permissions?: string[];
}

const messageDe = (e: unknown): string =>
  e instanceof ErreurApi ? e.messageUtilisateur : e instanceof Error ? e.message : 'Une erreur est survenue. Réessayez.';

export function SousVueDossier({
  idDossier,
  vue,
  fabriqueClient,
}: {
  idDossier: string;
  vue: Exclude<VueDossier, 'ensemble'>;
  fabriqueClient: () => Client;
}) {
  // Un seul client pour la vie de la sous-vue : ses lectures ne repartent pas à chaque rendu.
  const client = useMemo(fabriqueClient, [fabriqueClient]);
  const [entete, setEntete] = useState<EnTete | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<readonly string[]>([]);
  const [essai, setEssai] = useState(0);
  const source = useMemo(() => creerSourceComptesRendus(client, idDossier), [client, idDossier]);
  const sourceActes = useMemo(() => creerSourceActesNotaries(client, idDossier), [client, idDossier]);
  const sourceJournal = useMemo(() => creerSourceJournal(client, idDossier), [client, idDossier]);
  const sourceImport = useMemo(() => creerSourceImportContacts(client, idDossier), [client, idDossier]);
  const sourceVideos = useMemo(() => creerSourceVideos(client, idDossier), [client, idDossier]);

  useEffect(() => {
    let actif = true;
    setErreur(null);
    client
      .appeler<EnTete>('dossiers.lire', { parametres: { dossierId: idDossier } })
      .then((d) => actif && setEntete({ title: d.title, reference: d.reference }))
      .catch((e: unknown) => actif && setErreur(messageDe(e)));
    // Les permissions ne sont qu'un confort d'affichage : le serveur refuse de toute façon (403) ; leur panne n'empêche rien.
    client
      .appeler<Session>('auth.moi')
      .then((s) => actif && setPermissions(Array.isArray(s?.permissions) ? s.permissions : []))
      .catch(() => actif && setPermissions([]));
    return () => {
      actif = false;
    };
  }, [client, idDossier, essai]);

  return (
    <section
      data-ecran="D03"
      data-vue={vue}
      style={{ fontFamily: POLICE_EQUIPE, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}
    >
      {erreur ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
            {erreur}
          </p>
          <button type="button" style={styleBouton} onClick={() => setEssai((n) => n + 1)}>
            Réessayer
          </button>
        </div>
      ) : null}
      {entete ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, color: COULEURS.ardoise }}>{entete.reference}</span>
          <h1
            style={{
              margin: 0,
              fontFamily: "'Playfair Display', Georgia, serif",
              color: COULEURS.marine,
              fontSize: 28,
              overflowWrap: 'anywhere',
            }}
          >
            {entete.title}
          </h1>
        </div>
      ) : erreur ? null : (
        <p role="status">Chargement du dossier…</p>
      )}
      <NavSections dossierId={idDossier} actif={vue} />
      {vue === 'comptes-rendus' ? (
        <VueComptesRendus
          source={source}
          peutEcrire={permissions.includes(PERMISSION_ECRIRE)}
          peutValider={permissions.includes(PERMISSION_VALIDER)}
        />
      ) : null}
      {vue === 'journal' ? <VueJournal source={sourceJournal} /> : null}
      {vue === 'actes' ? <VueActesNotaries source={sourceActes} peutEcrire={permissions.includes(PERMISSION_ECRIRE_ACTES)} /> : null}
      {vue === 'videos' ? (
        <VueVideos
          source={sourceVideos}
          peutEcrire={permissions.includes(PERMISSION_ECRIRE_VIDEOS)}
          peutPartager={permissions.includes(PERMISSION_PARTAGER_VIDEOS)}
        />
      ) : null}
      {vue === 'import-contacts' ? (
        <VueImportContacts source={sourceImport} peutImporter={permissions.includes(PERMISSION_IMPORTER)} />
      ) : null}
    </section>
  );
}
